import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/API';
import SystemHeader from './SystemHeader';

const CampaignList = ({ userInfo, onLogout, totalPoints = 0, onRefreshPoints }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!userInfo || !userInfo.phoneNumber) {
        throw new Error('Không có thông tin người dùng');
      }

      console.log('🚀 Fetching campaigns for user:', userInfo.phoneNumber);
      
      // Gọi API để lấy danh sách chiến dịch của user
      const result = await API.getCampaignsForUser(userInfo.phoneNumber, 4);
      
      if (result.success && result.campaigns) {
        // Map API data to component format
        const mappedCampaigns = result.campaigns.map((campaign, index) => ({
          id: campaign.id || index + 1,
          code: campaign.code,
          name: campaign.name,
          description: `Chiến dịch ${campaign.name}`,
          status: getStatusFromData(campaign),
          startDate: campaign.startDate || new Date().toISOString(),
          endDate: campaign.endDate || new Date(Date.now() + 30*24*60*60*1000).toISOString(), // 30 days from now
          participants: 0, // API doesn't provide this field
          userPoints: campaign.userPoints || 0,
          userParticipatedAt: campaign.userParticipatedAt,
          userCompleted: campaign.userCompleted || false,
          points: campaign.points || 0,
          mainUrl: campaign.mainUrl,
          successUrl: campaign.successUrl,
          metadata: campaign.metadata,
          // Default image based on campaign code or generic
          image: getDefaultCampaignImage(campaign.code)
        }));
        
        console.log('✅ Campaigns loaded successfully:', mappedCampaigns);
        setCampaigns(mappedCampaigns);
      } else {
        console.log('⚠️ No campaigns found for user');
        setCampaigns([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching campaigns:', error);
      setError(error.message || 'Không thể tải danh sách chiến dịch');
      setLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo && userInfo.phoneNumber) {
      fetchCampaigns();
    }
  }, [userInfo, fetchCampaigns]);

  // Helper function to determine status from API data
  const getStatusFromData = (campaign) => {
    if (campaign.userCompleted) {
      return 'completed';
    }
    if (campaign.userParticipatedAt) {
      return 'active';
    }
    return 'upcoming';
  };

  // Helper function to get default campaign image
  const getDefaultCampaignImage = (campaignCode) => {
    const images = {
      'cd1': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=200&fit=crop',
      'cd2': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop',
      'cd3': 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=200&fit=crop'
    };
    return images[campaignCode] || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=200&fit=crop';
  };

  // Special program data - "Mua 10 tặng 1"
  const getSpecialProgram = () => ({
    id: 'special-program-1',
    code: 'mua10tang1',
    name: 'Chương trình mua 10 tặng 1',
    description: 'Chương trình đặc biệt mua 10 tặng 1 sản phẩm với 3 đợt',
    status: 'upcoming',
    startDate: '2025-09-16T00:00:00.000Z',
    endDate: null, // Không có ngày kết thúc chung
    participants: 0,
    userPoints: 0, // Chưa có điểm
    userParticipatedAt: '2025-09-16T00:00:00.000Z',
    userCompleted: false,
    points: null, // Không có điểm tối đa
    mainUrl: null,
    successUrl: null,
    metadata: { isSpecialProgram: true },
    image: '/images/logo-10in1.png', // Gift/special offer image
    isSpecialProgram: true,
    phases: [
      {
        phase: 'Đợt 1',
        endDate: '2025-10-15T00:00:00.000Z',
        status: 'upcoming'
      },
      {
        phase: 'Đợt 2', 
        endDate: '2025-11-15T00:00:00.000Z',
        status: 'upcoming'
      },
      {
        phase: 'Đợt 3',
        endDate: '2025-12-15T00:00:00.000Z',
        status: 'upcoming'
      }
    ]
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Đang tham gia</span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Đã hoàn thành</span>;
      case 'upcoming':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">Chưa tham gia</span>;
      case 'ended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Đã kết thúc</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Không xác định</span>;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const handleCampaignClick = (campaignCode) => {
    // Giữ nguyên token khi chuyển đến chiến dịch
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const params = new URLSearchParams({ token });
    
    // Special handling for "Mua 10 tặng 1" program - navigate to customer list
    if (campaignCode === 'mua10tang1') {
      navigate(`/khach-hang?${params.toString()}`);
      return;
    }
    
    navigate(`/chien-dich/${campaignCode}?${params.toString()}`);
  };

  // Kiểm tra xem user có đang ở path chiến dịch cụ thể không
  const isFromSpecificCampaign = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    return redirect && redirect.startsWith('/chien-dich/');
  };

  // Nếu user đang từ path chiến dịch cụ thể, hiển thị thông báo
  const renderCampaignRedirectNotice = () => {
    if (!isFromSpecificCampaign()) return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    const campaignCode = redirect?.split('/')[2];
    
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-brand-700">
                <span className="font-medium">Thông báo:</span> Bạn đã được chuyển đến danh sách chiến dịch. 
                Click vào <strong>chiến dịch {campaignCode}</strong> để tiếp tục.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải danh sách chiến dịch...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-600 font-medium">{error}</p>
          <button 
            onClick={fetchCampaigns}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  // totalPoints is now passed as a prop from App.js

  // Extra info for header - campaign count
  const extraInfo = (
    <div className="text-center">
      <p className="text-lg font-semibold text-gray-900">{campaigns.length} Chiến dịch</p>
      <p className="text-sm text-gray-500">Có sẵn cho bạn</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100">
      {/* System Header */}
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Danh sách chiến dịch"
        pageSubtitle="Chọn chiến dịch để tham gia"
        extraInfo={extraInfo}
        onRefreshPoints={onRefreshPoints}
      />



      {/* Campaign Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderCampaignRedirectNotice()}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Special Program - Always show first */}
          {(() => {
            const specialProgram = getSpecialProgram();
            return (
              <div 
                key={specialProgram.id}
                className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer overflow-hidden border-2 border-yellow-200"
                onClick={() => handleCampaignClick(specialProgram.code)}
              >
                {/* Campaign Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={specialProgram.image} 
                    alt={specialProgram.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute top-3 right-3">
                    {getStatusBadge(specialProgram.status)}
                  </div>
                </div>

                {/* Campaign Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                    {specialProgram.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {specialProgram.description}
                  </p>

                  {/* Campaign Details */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Ngày tham gia:</span>
                      <span className="font-medium text-gray-900">{formatDate(specialProgram.userParticipatedAt)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Điểm đã nhận:</span>
                      <span className="font-medium text-purple-600">-</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Điểm tối đa:</span>
                      <span className="font-medium text-gray-900">-</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Ngày bắt đầu:</span>
                      <span className="font-medium text-gray-900">{formatDate(specialProgram.startDate)}</span>
                    </div>
                    
                    <div className="mt-3">
                      <div className="text-sm text-gray-500 mb-2">Ngày kết thúc:</div>
                      <div className="space-y-2">
                        {specialProgram.phases.map((phase, index) => (
                          <div key={index} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                            <span className="font-medium text-gray-700">{phase.phase}:</span>
                            <span className="text-gray-900">{formatDate(phase.endDate)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-6">
                    <button className="w-full font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600">
                      Xem chương trình
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Regular Campaigns */}
          {campaigns.map((campaign) => (
            <div 
              key={campaign.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer overflow-hidden"
              onClick={() => handleCampaignClick(campaign.code)}
            >
              {/* Campaign Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={campaign.image} 
                  alt={campaign.name}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute top-3 right-3">
                  {getStatusBadge(campaign.status)}
                </div>
              </div>

              {/* Campaign Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                  {campaign.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {campaign.description}
                </p>

                {/* Campaign Details */}
                <div className="space-y-3">
                  {campaign.userParticipatedAt && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Ngày tham gia:</span>
                      <span className="font-medium text-gray-900">{formatDate(campaign.userParticipatedAt)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Điểm đã nhận:</span>
                    <span className="font-medium text-purple-600">{campaign.userPoints.toLocaleString()} điểm</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Điểm tối đa:</span>
                    <span className="font-medium text-gray-900">{campaign.points.toLocaleString()} điểm</span>
                  </div>

                  {campaign.startDate && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Ngày bắt đầu:</span>
                      <span className="font-medium text-gray-900">{formatDate(campaign.startDate)}</span>
                    </div>
                  )}
                  
                  {campaign.endDate && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Ngày kết thúc:</span>
                      <span className="font-medium text-gray-900">{formatDate(campaign.endDate)}</span>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                <div className="mt-6">
                  <button className={`w-full font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 ${
                    campaign.status === 'completed' 
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'
                      : campaign.status === 'active'
                      ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                  }`}>
                    {campaign.status === 'completed' ? 'Xem kết quả' :
                     campaign.status === 'active' ? 'Tiếp tục tham gia' :
                     'Bắt đầu tham gia'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {campaigns.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có chiến dịch nào</h3>
            <p className="text-gray-500">Hiện tại không có chiến dịch nào đang diễn ra hoặc sắp diễn ra.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignList;
